import { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { AppLayout } from "@/components/layout/AppLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Copy, Download, Share2, BarChart3, DollarSign, MousePointerClick, CheckCircle2, QrCode, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO, subDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { SEO } from "@/components/SEO";

type Partner = {
  id: string; name: string; email: string; code: string; status: string;
  commission_amount_cents: number | null; pix_key: string | null;
};
type Conversion = {
  id: string; status: string; commission_amount_cents: number;
  gross_amount_cents: number | null; created_at: string; approved_at: string | null; paid_at: string | null;
};
type Click = { id: string; created_at: string };
type Payout = { id: string; total_amount_cents: number; conversion_count: number; status: string; created_at: string; paid_at: string | null; payment_proof_url: string | null };

const fmtBRL = (c?: number | null) => ((c ?? 0) / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; variant: "default" | "secondary" | "outline" | "destructive" }> = {
    pending: { label: "Em análise", variant: "secondary" },
    approved: { label: "Aprovada", variant: "default" },
    rejected: { label: "Rejeitada", variant: "destructive" },
    refunded: { label: "Estornada", variant: "outline" },
    paid: { label: "Paga", variant: "default" },
  };
  const m = map[status] || { label: status, variant: "outline" as const };
  return <Badge variant={m.variant}>{m.label}</Badge>;
}

export default function PartnerPanel() {
  const { user, loading } = useAuth();
  const [partner, setPartner] = useState<Partner | null>(null);
  const [conversions, setConversions] = useState<Conversion[]>([]);
  const [clicks, setClicks] = useState<Click[]>([]);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      setBusy(true);
      const { data: p } = await supabase.from("referral_partners").select("*").eq("user_id", user.id).maybeSingle();
      if (!p) { setPartner(null); setBusy(false); return; }
      setPartner(p as Partner);
      const since = subDays(new Date(), 90).toISOString();
      const [{ data: c }, { data: cl }, { data: po }] = await Promise.all([
        supabase.from("referral_conversions").select("*").eq("partner_id", p.id).order("created_at", { ascending: false }),
        supabase.from("referral_clicks").select("id, created_at").eq("partner_id", p.id).gte("created_at", since).order("created_at", { ascending: false }),
        supabase.from("referral_payouts").select("*").eq("partner_id", p.id).order("created_at", { ascending: false }),
      ]);
      setConversions((c as Conversion[]) || []);
      setClicks((cl as Click[]) || []);
      setPayouts((po as Payout[]) || []);
      setBusy(false);
    })();
  }, [user]);

  const referralUrl = useMemo(() => partner ? `https://careos.life/?ref=${partner.code}` : "", [partner]);
  const qrUrl = useMemo(() => referralUrl ? `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(referralUrl)}` : "", [referralUrl]);

  const stats = useMemo(() => {
    const approved = conversions.filter(c => c.status === "approved").reduce((s, c) => s + c.commission_amount_cents, 0);
    const pending = conversions.filter(c => c.status === "pending").reduce((s, c) => s + c.commission_amount_cents, 0);
    const paid = conversions.filter(c => c.status === "paid").reduce((s, c) => s + c.commission_amount_cents, 0);
    return { clicks: clicks.length, conversions: conversions.length, available: approved, pending, paid };
  }, [conversions, clicks]);

  const chartData = useMemo(() => {
    const days: Record<string, { date: string; clicks: number; conversions: number }> = {};
    for (let i = 29; i >= 0; i--) {
      const d = format(subDays(new Date(), i), "yyyy-MM-dd");
      days[d] = { date: format(subDays(new Date(), i), "dd/MM"), clicks: 0, conversions: 0 };
    }
    clicks.forEach(c => { const k = c.created_at.slice(0, 10); if (days[k]) days[k].clicks++; });
    conversions.forEach(c => { const k = c.created_at.slice(0, 10); if (days[k]) days[k].conversions++; });
    return Object.values(days);
  }, [clicks, conversions]);

  const downloadCsv = async () => {
    const { data: sess } = await supabase.auth.getSession();
    const token = sess.session?.access_token;
    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/referral-export`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { toast.error("Falha ao exportar."); return; }
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `conversoes-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
  };

  if (loading || busy) {
    return (
      <AppLayout title="Espaço do parceiro">
        <div className="p-4 space-y-4">
          <Skeleton className="h-24" />
          <Skeleton className="h-32" />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Skeleton className="h-20" /><Skeleton className="h-20" />
            <Skeleton className="h-20" /><Skeleton className="h-20" />
          </div>
        </div>
      </AppLayout>
    );
  }
  if (!user) return <Navigate to="/auth?redirect=/parceiros/painel" replace />;

  if (!partner) {
    return (
      <AppLayout title="Espaço do parceiro">
        <div className="p-4">
          <Card className="max-w-md mx-auto">
            <CardHeader>
              <CardTitle>Você ainda não é parceiro</CardTitle>
              <CardDescription>
                Não encontramos um cadastro de parceiro vinculado ao seu e-mail ({user.email}).
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild><Link to="/parceiros/cadastro">Cadastrar como parceiro</Link></Button>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  if (partner.status !== "active") {
    return (
      <AppLayout title="Espaço do parceiro">
        <div className="p-4">
          <Card className="max-w-md mx-auto">
            <CardHeader>
              <CardTitle>Cadastro {partner.status === "pending_approval" ? "em análise" : partner.status}</CardTitle>
              <CardDescription>Avisaremos por e-mail assim que for aprovado.</CardDescription>
            </CardHeader>
          </Card>
        </div>
      </AppLayout>
    );
  }

  const waText = encodeURIComponent(`Conheça o CareOS, plataforma para cuidar de quem você ama: ${referralUrl}`);

  return (
    <AppLayout title="Espaço do parceiro">
      <SEO title="Painel do parceiro CareOS" description="Painel do parceiro CareOS" noIndex />
      <div className="p-4 space-y-4">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold truncate">Olá, {partner.name}</h1>
            <p className="text-sm text-muted-foreground">
              Seu código: <Badge variant="secondary" className="font-mono">{partner.code}</Badge>
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={downloadCsv}>
            <Download className="h-4 w-4 mr-2" />Exportar CSV
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KPI icon={MousePointerClick} label="Cliques (90 dias)" value={stats.clicks} />
          <KPI icon={CheckCircle2} label="Conversões" value={stats.conversions} />
          <KPI icon={DollarSign} label="Disponível" value={fmtBRL(stats.available)} highlight />
          <KPI icon={DollarSign} label="Aguardando aprovação" value={fmtBRL(stats.pending)} muted />
        </div>

        {/* Link & QR */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Share2 className="h-4 w-4 text-primary" />Seu link de indicação
            </CardTitle>
            <CardDescription>Compartilhe este link. Toda nova assinatura paga gera comissão.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2 flex-wrap">
              <code className="flex-1 min-w-0 truncate bg-muted px-3 py-2 rounded-md text-sm">{referralUrl}</code>
              <Button onClick={() => { navigator.clipboard.writeText(referralUrl); toast.success("Link copiado!"); }}>
                <Copy className="h-4 w-4 mr-2" />Copiar
              </Button>
            </div>

            <div className="grid gap-4 sm:grid-cols-[auto,1fr] sm:items-center">
              {qrUrl && (
                <div className="flex flex-col items-center gap-2 p-4 bg-muted/40 rounded-lg border">
                  <img src={qrUrl} alt="QR Code do link de indicação" className="h-40 w-40 rounded bg-background" />
                  <Button variant="outline" size="sm" asChild>
                    <a href={qrUrl} download={`qrcode-${partner.code}.png`}>
                      <QrCode className="h-4 w-4 mr-2" />Baixar QR Code
                    </a>
                  </Button>
                </div>
              )}
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Use o QR Code em panfletos, cartões e materiais impressos. Para divulgar online, use o WhatsApp:
                </p>
                <Button variant="outline" size="sm" asChild>
                  <a target="_blank" rel="noreferrer" href={`https://wa.me/?text=${waText}`}>
                    <MessageCircle className="h-4 w-4 mr-2" />Compartilhar no WhatsApp
                  </a>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <BarChart3 className="h-4 w-4 text-primary" />Últimos 30 dias
            </CardTitle>
          </CardHeader>
          <CardContent style={{ height: 280 }}>
            <ResponsiveContainer>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                <Line type="monotone" dataKey="clicks" stroke="hsl(var(--primary))" name="Cliques" strokeWidth={2} />
                <Line type="monotone" dataKey="conversions" stroke="hsl(var(--success))" name="Conversões" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Conversions */}
        <Card>
          <CardHeader><CardTitle className="text-base">Conversões</CardTitle></CardHeader>
          <CardContent>
            {conversions.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nenhuma conversão ainda.</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Comissão</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {conversions.slice(0, 50).map(c => (
                      <TableRow key={c.id}>
                        <TableCell>{format(parseISO(c.created_at), "dd/MM/yyyy", { locale: ptBR })}</TableCell>
                        <TableCell><StatusBadge status={c.status} /></TableCell>
                        <TableCell className="text-right font-medium">{fmtBRL(c.commission_amount_cents)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payouts */}
        <Card>
          <CardHeader><CardTitle className="text-base">Pagamentos</CardTitle></CardHeader>
          <CardContent>
            {payouts.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nenhum pagamento ainda.</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead>Comprovante</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payouts.map(p => (
                      <TableRow key={p.id}>
                        <TableCell>{format(parseISO(p.created_at), "dd/MM/yyyy", { locale: ptBR })}</TableCell>
                        <TableCell><Badge variant="outline">{p.status}</Badge></TableCell>
                        <TableCell className="text-right font-medium">{fmtBRL(p.total_amount_cents)}</TableCell>
                        <TableCell>
                          {p.payment_proof_url
                            ? <a className="text-primary underline" target="_blank" rel="noreferrer" href={p.payment_proof_url}>Ver</a>
                            : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}

function KPI({ icon: Icon, label, value, muted, highlight }: { icon: any; label: string; value: string | number; muted?: boolean; highlight?: boolean }) {
  return (
    <Card>
      <CardContent className="p-4 flex items-center gap-3">
        <div
          className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${
            muted ? "bg-muted text-muted-foreground" : highlight ? "bg-success/10 text-success" : "bg-primary/10 text-primary"
          }`}
        >
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-lg font-semibold leading-tight truncate">{value}</p>
          <p className="text-xs text-muted-foreground truncate">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}
